/**
 * Team Inbox — thread/list furniture (UI Spec §7.3 + §9 states):
 * date pills, unread divider, scroll-to-bottom FAB, skeleton rows,
 * empty states.
 */

export function DatePill({ label }: { label: string }) {
  return (
    <div className="ti-datepill">
      <span>{label}</span>
    </div>
  );
}

export function UnreadDivider({ count }: { count: number }) {
  return (
    <div className="ti-unread-divider" role="separator">
      {count} UNREAD MESSAGE{count === 1 ? '' : 'S'}
    </div>
  );
}

export function ScrollToBottomFab({
  pending,
  onClick,
}: {
  pending: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="ti-fab"
      onClick={onClick}
      aria-label="Scroll to latest message"
    >
      ↓{pending > 0 && <span className="n">{pending}</span>}
    </button>
  );
}

/** Skeleton rows — exactly the row geometry, no spinners-in-space (§9). */
export function SkeletonRows({ count = 6 }: { count?: number }) {
  return (
    <div aria-hidden role="presentation">
      {Array.from({ length: count }, (_, i) => (
        <div className="ti-skel-row" key={i}>
          <span className="ti-skel av" />
          <span style={{ flex: 1 }}>
            <span className="ti-skel l1" style={{ display: 'block' }} />
            <span className="ti-skel l2" style={{ display: 'block' }} />
          </span>
        </div>
      ))}
    </div>
  );
}

/** Slim centred §7.3 system line — allocation/escalation transparency. */
export function SystemLine({ text }: { text: string }) {
  return (
    <div className="ti-sysline" role="note">
      <span>{text}</span>
    </div>
  );
}

/** "Khilendra is replying…" soft-lock banner (R-C2), above the composer. */
export function ClaimBanner({ text }: { text: string }) {
  return (
    <div className="ti-claim" role="status" aria-live="polite" data-testid="ti-claim-banner">
      <span className="pulse" aria-hidden>
        ✎
      </span>
      {text}
    </div>
  );
}

/** Friendly empty state — never a blank pane (§9). */
export function EmptyState({
  icon,
  children,
}: {
  icon: string;
  children: React.ReactNode;
}) {
  return (
    <div className="ti-empty">
      <span className="big" aria-hidden>
        {icon}
      </span>
      {children}
    </div>
  );
}
